import { useCallback } from 'react';
import { useFormShowContext } from 'framework/form';
import { Box, Grid2 as Grid, Typography, TypographyProps } from '@mui/material';
import {
  Filters,
  FiltersLayout,
  EditableListDataGridLayout,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
} from 'framework/list';
import RiskField from './RiskField';
import { TextFieldWithPrefix, getAnchorRowStyle, FormattedTextField } from 'framework/ui/field';
import { AppIcon, PriorityIcon, SeverityIcon, PlusIcon, Tooltip, TLetterIcon } from 'framework/ui/mui-builder';
import { useLocation } from 'react-router-dom';
import { defaultsDeep } from 'lodash';
import RiskOwnerField from './RiskOwnerField';
import LastUpdateField from './fields/LastUpdateField';
import IconButton from '@mui/material/IconButton';
import { SxProps } from '@mui/system';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import { EmployeeFilter } from 'domain/shared/employee';
import {
  PROJECT_PROBABILITY_OPTIONS,
  RISK_MITIGATION_STRATEGY_OPTIONS,
  RISK_STATUS_OPTIONS,
  RISK_TYPE_OPTIONS,
  CREATE_RISK,
  PROJECT_CREATE_RISK,
  StatusFilter,
  useNewProjectUrl,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';
import { EMPTY_VALUE_INDICATOR, openPageInNewTab } from 'framework/utils';
import RiskStatusField from './fields/StatusField';
import RiskSortableButton from './RiskSortableButton';

type RisksProps = {
  accordionSx?: SxProps;
  titleSx?: SxProps;
  titleVariant?: TypographyProps['variant'];
  gridSx?: SxProps;
};

const Risks = ({ titleSx, titleVariant = 'h4', accordionSx, gridSx = { pl: 2 } }: RisksProps) => {
  const { isEditMode } = useFormShowContext();
  const { isGranted } = useAuthorization();
  const { hash } = useLocation();
  const { getCreateRiskUrl } = useNewProjectUrl();
  const { data: projectData } = useShowContext();

  const canCreateRisk = isGranted([PROJECT_CREATE_RISK, CREATE_RISK]);

  const renderActions = useCallback(() => {
    if (canCreateRisk) {
      return [
        <IconButton sx={{ p: 0, ml: 1 }} key="add" onClick={() => openPageInNewTab(getCreateRiskUrl(projectData?.id!))}>
          <PlusIcon />
        </IconButton>,
      ];
    }
    return null;
  }, [getCreateRiskUrl, projectData.id, canCreateRisk]);

  const filters = (
    <Filters>
      <FiltersLayout>
        <StatusFilter source="status" label={'Status'} options={RISK_STATUS_OPTIONS} data-testid="status-filter" />
        <StatusFilter
          dropdownLabel={'Strategy defined'}
          source="mitigationStrategyStatus"
          label={'Strategy'}
          options={RISK_MITIGATION_STRATEGY_OPTIONS}
          data-testid="strategy-filter"
        />
        <StatusFilter
          dropdownLabel={'Probability'}
          source="probability"
          label={'Probability'}
          options={PROJECT_PROBABILITY_OPTIONS}
          data-testid="probability-filter"
        />
        <StatusFilter
          source="severity"
          dropdownLabel="Severity"
          label={'Severity'}
          options={PROJECT_PROBABILITY_OPTIONS}
        />
        <EmployeeFilter label="Risk owner" source="riskOwner" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <ProjectSectionAccordion
      sx={accordionSx}
      defaultExpanded
      title={
        <Grid
          container
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', ...gridSx }}
        >
          <Grid size={2.4}>
            <Typography variant={titleVariant} color="primary" sx={titleSx}>
              Risks
            </Typography>
          </Grid>
          <Grid size={0.5}>
            <RiskSortableButton />
          </Grid>
          <Grid
            size="auto"
            alignItems="center"
            sx={{
              '.add-filter-button__wrapper': { mb: 0 },
              '.filter-item-wrapper': { mb: 0 },
              '.filters-reset-btn': { mb: 0 },
            }}
          >
            {!isEditMode ? filters : <></>}
          </Grid>
          <Grid size="grow" />
          <Grid size="auto" display="flex" alignItems="center" pr={1}>
            {renderActions()}
          </Grid>
        </Grid>
      }
    >
      <EditableListDataGridLayout
        data-testid="list-data-grid-layout"
        rowProps={data =>
          defaultsDeep(getAnchorRowStyle(data, hash), {
            sx: {
              alignItems: 'center',
            },
          })
        }
        getTableCells={isEditModeLayout =>
          Promise.resolve([
            {
              type: EDITABLE_LIST_GROUP,
              component: Grid,
              componentProps: {
                container: true,
                alignItems: 'center',
                justifyContent: 'space-between',
              },
              gridCellProps: {
                size: 12,
              },
              children: [
                {
                  type: EDITABLE_LIST_CUSTOM,
                  isEditor: false,
                  component: RiskField,
                  componentProps: {
                    projectId: 'projectId',
                    idSource: 'id',
                    aliasSource: 'alias',
                    newRiskForm: true,
                  },
                  gridCellProps: {
                    size: 0.5,
                    sx: { '.MuiFormControl-root': { mb: 0 }, display: 'flex', justifyContent: 'flex-start' },
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'title',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (str: string) =>
                      str && str.length > 60 ? (
                        <Tooltip title={str}>
                          <div>
                            <Typography
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                              }}
                            >
                              {str}
                            </Typography>
                          </div>
                        </Tooltip>
                      ) : (
                        str
                      ),
                  },
                  gridCellProps: {
                    size: 2.3,
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'type',
                  isEditor: false,
                  component: TextFieldWithPrefix,
                  componentProps: {
                    label: '',
                    format: (value: string) => (value ? RISK_TYPE_OPTIONS[value] : EMPTY_VALUE_INDICATOR),
                    tooltipProps: {
                      title: 'Risk type',
                      children: (
                        <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                          <TLetterIcon />
                        </Box>
                      ),
                    },
                  },
                  gridCellProps: {
                    size: 1.5,
                    alignItems: 'center',
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'probability',
                  isEditor: false,
                  component: TextFieldWithPrefix,
                  componentProps: {
                    label: '',
                    format: (value: string) =>
                      PROJECT_PROBABILITY_OPTIONS[value as keyof typeof PROJECT_PROBABILITY_OPTIONS] || value,
                    tooltipProps: {
                      title: 'Probability',
                      children: (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <PriorityIcon />
                        </div>
                      ),
                    },
                  },
                  gridCellProps: {
                    size: 1.5,
                    alignItems: 'center',
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'severity',
                  isEditor: false,
                  component: TextFieldWithPrefix,
                  componentProps: {
                    label: '',
                    format: (value: string) =>
                      PROJECT_PROBABILITY_OPTIONS[value as keyof typeof PROJECT_PROBABILITY_OPTIONS] || value,
                    tooltipProps: {
                      title: 'Severity',
                      children: (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <SeverityIcon />
                        </div>
                      ),
                    },
                  },
                  gridCellProps: {
                    size: 1.5,
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'riskOwner',
                  isEditor: false,
                  component: RiskOwnerField,
                  componentProps: { label: '' },
                  gridCellProps: {
                    size: 1.5,
                  },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  source: 'status',
                  isEditor: false,
                  component: RiskStatusField,
                  componentProps: {
                    statusSource: 'status',
                    isEscalatedSource: 'isEscalated',
                  },
                  gridCellProps: {
                    size: 1,
                  },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  source: 'updatedAt',
                  isEditor: false,
                  component: LastUpdateField,
                  componentProps: {
                    createSource: 'createdAt',
                    updateSource: 'updatedAt',
                    isRiskActive: true,
                  },
                  gridCellProps: {
                    size: 1.5,
                    alignItems: 'center',
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'isIncludedInCustomerReport',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (str: string) => (
                      <Tooltip title={str ? 'Visible for client' : 'Not visible for client'}>
                        <span>
                          {str ? (
                            <AppIcon icon="verified-document" viewBox="0 0 16 16" color="success" />
                          ) : (
                            <AppIcon icon="rejected-document" viewBox="0 0 16 16" />
                          )}
                        </span>
                      </Tooltip>
                    ),
                  },
                  gridCellProps: {
                    size: 0.5,
                    pr: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  },
                },
              ],
            },
          ])
        }
      />
    </ProjectSectionAccordion>
  );
};
export default Risks;
